import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Box, Divider,
} from '@mui/material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase'; // Adjust according to your file structure
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const CalculateMonthlyHoursDialog = ({ open, onClose, user }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [employeeMonthlyHours, setEmployeeMonthlyHours] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default rows per page

  const fetchEmployeeMonthlyHours = useCallback(async () => {
    try {
      const employeeMonthlyHoursRef = collection(db, 'employeeMonthlyHours');
      const q = query(employeeMonthlyHoursRef, where('employeeId', '==', user.id));
      const querySnapshot = await getDocs(q);
      const monthlyHoursData = querySnapshot.docs.map(doc => doc.data());

      // Sort the data to display the most recent month on top
      monthlyHoursData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setEmployeeMonthlyHours(monthlyHoursData);
    } catch (error) {
      console.error("Error fetching employee monthly hours: ", error);
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchEmployeeMonthlyHours();  // Fetch employee monthly hours
    }
  }, [open, fetchEmployeeMonthlyHours]);

  const handleClose = () => {
    onClose();
    setEmployeeMonthlyHours([]);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const handlePageChange = (event, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Employee Monthly Hours for {user.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            <AccountCircleIcon fontSize="small" /> Employee Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" gutterBottom>
          <strong>Employee ID:</strong> {user.id}
          </Typography>
          <Typography variant="body1" gutterBottom>
          <strong>Joining Date:</strong> {user.joiningDate}
          </Typography>
          </Box>

          {/* Render Employee Monthly Hours Table */}
          <Box sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            <AccessTimeIcon fontSize="small" /> Monthly Hours Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Employee ID</b></TableCell>
                <TableCell><b>Month</b></TableCell>
                <TableCell><b>Monthly Hours</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeMonthlyHours
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>{entry.employeeId}</TableCell>
                    <TableCell>{entry.month}</TableCell>
                    <TableCell>{entry.monthlyHours}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={employeeMonthlyHours.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ color: 'red' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarMessage.includes('Error') ? 'error' : 'success'}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CalculateMonthlyHoursDialog;
